import { computed, reactive } from 'vue';
import { useRoute } from 'vue-router';
import { brandIdentityFor } from '../ui/brand-identity.js';
import { useSession } from './session.js';
import { useSkin } from './skin.js';
import { surfaceOf, useTenantStatus, type BrandSurface } from './tenant.js';

/** 平台 ICP 备案号(构建期配置;机构自有域名用机构页脚里填的) */
const PLATFORM_ICP = (import.meta.env['VITE_PLATFORM_ICP'] as string | undefined) ?? '';

/**
 * 页面上用的品牌(规格 2.4.3 · 原型 platform/modules「品牌与白标」· 验收 B2-4):
 * - 平台站点:皮肤的平台品牌;
 * - 机构站点:机构品牌(按模块配置时按当前页面所属的面:学习平台 / 考试端 / 大赛门户);
 *   没开白标 → 同时露出平台标识与推广入口(poweredBy);开了白标 → 不出平台标识;
 * - 页脚合规行(ICP 备案号 + 未成年人保护)始终保留,机构配置删不掉。
 */
export function useBrand(fixed?: BrandSurface) {
  const skin = useSkin();
  const tenant = useTenantStatus();
  const session = useSession();
  const route = useRoute();
  // 站点品牌只查一次(登录页、官网这些没有只读横幅的页面也要拿到机构品牌)
  void tenant.load();
  const surface = computed(() => fixed ?? surfaceOf(route.path));
  const isOrg = computed(() => !!tenant.info?.brand);
  const face = computed(() => (isOrg.value ? tenant.faceOf(surface.value) : null));
  const whiteLabel = computed(() => !!tenant.info?.brand?.whiteLabel?.site);
  /**
   * 名字、方块里的字 / 图形、口号出谁的,规矩在 ui/brand-identity.ts:
   * 机构站点出机构自己的(方块里是机构名的第一个字,或它上传的 Logo),平台身份才画平台 Logo 的图形
   * (components/BrandGlyph.vue);站点信息还没取到时全部空着,不先露出平台的。
   */
  const identity = computed(() =>
    brandIdentityFor({
      settled: tenant.settled,
      orgName: face.value?.displayName ?? null,
      whiteLabel: whiteLabel.value,
      // 站点码(子域名 / 手动选的站点)已经说明这是机构站点:站点信息取失败时也不拿平台的品牌顶上
      orgSite: !session.isPlatformSite,
      platform: { name: skin.brand, mark: skin.brandMark, slogan: skin.slogan, tagline: skin.tagline },
    }),
  );
  const name = computed(() => identity.value.name);
  const mark = computed(() => identity.value.mark);
  const logoUrl = computed(() => face.value?.logoUrl ?? null);
  const glyph = computed(() => identity.value.glyph);
  const adminWhiteLabel = computed(() => !!tenant.info?.brand?.whiteLabel?.admin);
  /** 机构站点没开白标时显示「平台名」作为技术提供方;平台站点 / 白标为空 */
  const poweredBy = computed(() => (isOrg.value && !whiteLabel.value ? skin.brand : null));
  const footer = computed(() => tenant.info?.brand?.footer ?? null);
  /** 合规行:ICP(机构填了用机构的,否则平台的)· 公安备案 · 未成年人保护 */
  const compliance = computed(() => ({
    icp: footer.value?.icp || PLATFORM_ICP || null,
    police: footer.value?.police || null,
  }));
  const welcome = computed(() => tenant.info?.brand?.welcome ?? null);
  // reactive 包一层:模板与脚本里直接 brand.name,不用 .value
  return reactive({
    surface,
    isOrg,
    name,
    mark,
    glyph,
    logoUrl,
    whiteLabel,
    adminWhiteLabel,
    poweredBy,
    footer,
    compliance,
    welcome,
    platformName: computed(() => skin.brand),
    slogan: computed(() => identity.value.slogan),
    /** 导航上品牌名下面那行小字:平台站点才有;机构站点、站点信息还没取到时为空 */
    tagline: computed(() => identity.value.tagline),
  });
}
